import { useEffect, useState } from 'react';

import {
  extractUniqueTextEntitiesFromGIQ,
  processCustomTextEntities,
  combineTextEntities,
} from 'views/App/Portal/Policy/DraftManuals/utils/textEntityProcessor';
import type { TextEntity } from 'views/App/Portal/Policy/DraftManuals/utils/textEntityProcessor';
import useDraftManualGIQs from 'views/App/Portal/Policy/DraftManuals/_data_sources/giq/useDraftManualGIQs';
import { getCustomTextEntities } from 'views/App/Portal/Policy/DraftManuals/_data_sources/customTextEntities/useCustomTextEntities';
import { getDraftManual } from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftManual';

export function useManualTextEntities(manualId: string | undefined): {
  textEntities: TextEntity[];
  isLoadingTextEntities: boolean;
} {
  const [textEntities, setTextEntities] = useState<TextEntity[]>([]);
  const [isLoadingTextEntities, setIsLoadingTextEntities] = useState(false);

  const giqQuery = useDraftManualGIQs(manualId || '');

  useEffect(() => {
    const fetchTextEntities = async () => {
      if (!manualId) {
        setTextEntities([]);
        return;
      }

      setIsLoadingTextEntities(true);
      try {
        const draftManual = await getDraftManual(manualId);

        if (draftManual?.manualLevel !== 'AGENCY') {
          setTextEntities([]);
          return;
        }

        const giqTextEntities = giqQuery.data?.questions
          ? extractUniqueTextEntitiesFromGIQ(giqQuery.data.questions)
          : [];

        let customTextEntities: TextEntity[] = [];
        try {
          const customTextEntitiesResponse = await getCustomTextEntities(manualId);
          customTextEntities = processCustomTextEntities(customTextEntitiesResponse.content);
        } catch (error) {
          console.error('Failed to fetch custom text entities:', error);
        }

        const allTextEntities = combineTextEntities(giqTextEntities, customTextEntities);
        setTextEntities(allTextEntities);
      } catch (error) {
        console.error('Failed to fetch text entities:', error);
        setTextEntities([]);
      } finally {
        setIsLoadingTextEntities(false);
      }
    };

    fetchTextEntities();
  }, [manualId, giqQuery.data]);

  return { textEntities, isLoadingTextEntities };
}
